import { toast } from 'sonner'
import { Loader2, AlertTriangle, FolderTree } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from '@/components/ui/dialog'
import { useDeleteDepartment } from '@/hooks/useDepartments'
import type { ApiDepartment } from '@/types/department'
import { showError } from '@/lib/error'

interface DeleteDepartmentDialogProps {
	open: boolean
	onOpenChange: (open: boolean) => void
	department: ApiDepartment | null
	onSuccess?: () => void
}

export function DeleteDepartmentDialog({
	open,
	onOpenChange,
	department,
	onSuccess,
}: DeleteDepartmentDialogProps) {
	const deleteMutation = useDeleteDepartment()

	const hasChildren = department?.children && department.children.length > 0
	const childrenCount = department?.children_count || 0

	const handleDelete = async () => {
		if (!department) return

		try {
			await deleteMutation.mutateAsync(department.id)
			toast.success('部门删除成功')
			onOpenChange(false)
			onSuccess?.()
		} catch (error) {
			console.error('删除部门失败:', error)
			showError(error, '删除部门失败')
		}
	}

	const handleClose = () => {
		onOpenChange(false)
	}

	if (!department) return null

	return (
		<Dialog open={open} onOpenChange={handleClose}>
			<DialogContent className="sm:max-w-[450px]">
				<DialogHeader>
					<DialogTitle className="flex items-center gap-2 text-destructive">
						<AlertTriangle className="h-5 w-5" />
						删除部门
					</DialogTitle>
					<DialogDescription>
						确定要删除部门「{department.name}」吗？此操作将级联删除所有子部门。
					</DialogDescription>
				</DialogHeader>

				<div className="space-y-4">
					{/* 警告信息 - 子部门 */}
					{(hasChildren || childrenCount > 0) && (
						<div className="flex items-start gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
							<FolderTree className="h-4 w-4 mt-0.5 shrink-0" />
							<span>
								该部门下还有子部门，删除后子部门也将被删除。
							</span>
						</div>
					)}

					<p className="text-sm text-muted-foreground">
						删除后无法恢复，请确认操作。
					</p>
				</div>

				<DialogFooter>
					<Button type="button" variant="outline" onClick={handleClose}>
						取消
					</Button>
					<Button
						type="button"
						variant="destructive"
						onClick={handleDelete}
						disabled={deleteMutation.isPending}
					>
						{deleteMutation.isPending && (
							<Loader2 className="mr-2 h-4 w-4 animate-spin" />
						)}
						删除
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
